// Salidas de hoy (PP-64, rediseño): los viajes de la empresa del dia
// operativo de cada planta, agrupados por hora de salida, con vehiculo,
// conductor y estado. Es informativo: va en el segundo nivel del Inicio, con
// las que todavia no salieron primero y acotadas, y las que ya salieron
// plegadas. El dia entero esta en Viajes.
//
// El vehiculo y el conductor se nombran con la nomina de la planta (codigo y
// patente, codigo y nombre); nunca con un id tecnico (PB-29).
import type { ProviderMultiSiteTrip } from "@ucorp/contracts";
import { Card, PanelHeader } from "../components/ui/card.js";
import { Failure } from "../components/ui/failure.js";
import { SkeletonTable } from "../components/ui/skeleton.js";
import { accessKey, asProviderTrip, departureGroups, HOME_INFO_ROWS, splitDepartures, topN, type DepartureGroup, type SiteRoster } from "../logic/home.js";
import { formatClock, tripTitle } from "../logic/requests.js";
import { ASSIGNMENT_STATUS_LABEL, TRIP_STATUS_LABEL } from "../requests/labels.js";
import type { ReadState } from "../multi-site/usePolled.js";
import { StaleTag } from "./StaleTag.js";

interface Props {
  id: string;
  state: ReadState<unknown>;
  rows: readonly ProviderMultiSiteTrip[];
  timezones: ReadonlyMap<string, string>;
  rosters: ReadonlyMap<string, SiteRoster> | undefined;
  truncated: boolean;
  now: number;
  /** A donde lleva "Ver el día en Viajes". */
  tripsHref: string;
  onRetry: () => void;
}

function pair(row: ProviderMultiSiteTrip, rosters: Props["rosters"]): { vehicle: string; driver: string } {
  const roster = rosters?.get(accessKey(row));
  const vehicle = roster?.vehicles.find((v) => v.id === row.vehicleId);
  const driver = roster?.drivers.find((d) => d.id === row.driverId);
  return {
    vehicle: row.vehicleId === undefined ? "Sin asignar" : vehicle === undefined ? "Asignado" : `${vehicle.code} · ${vehicle.plate}`,
    driver: row.driverId === undefined ? "Sin asignar" : driver === undefined ? "Asignado" : `${driver.code} · ${driver.name}`,
  };
}

function stateLabel(row: ProviderMultiSiteTrip): string {
  if (row.status !== "scheduled" || row.assignmentStatus === "assigned") return TRIP_STATUS_LABEL[row.status];
  return ASSIGNMENT_STATUS_LABEL[row.assignmentStatus];
}

function stateDot(row: ProviderMultiSiteTrip): string {
  if (row.status === "in_progress") return "bg-ok";
  if (row.status === "completed" || row.status === "cancelled") return "bg-texto-debil";
  if (row.assignmentStatus === "assigned") return "bg-acero-claro";
  if (row.assignmentStatus === "offered" || row.assignmentStatus === "accepted") return "bg-ambar";
  return "bg-texto-debil";
}

function Groups({ groups, timezones, rosters, muted }: { groups: readonly DepartureGroup[]; timezones: Props["timezones"]; rosters: Props["rosters"]; muted?: boolean }): React.JSX.Element {
  return (
    <>
      {groups.map((group, index) => (
        <div key={`${group.label}-${String(index)}`}>
          <h3 className="tabular m-0 border-t border-divisor bg-hundida/60 px-3.5 py-1 text-micro font-medium uppercase tracking-[0.1em] text-texto-debil first:border-t-0">
            {group.label}
          </h3>
          <ul aria-label={`Salidas ${group.label}`} className="m-0 flex list-none flex-col p-0">
            {group.rows.map((row) => {
              const timezone = timezones.get(row.siteId);
              const clock = row.summary?.departureAt === undefined || timezone === undefined ? "" : formatClock(row.summary.departureAt, timezone);
              const { vehicle, driver } = pair(row, rosters);
              return (
                <li
                  key={`${row.siteId}:${row.tripId}`}
                  className={`grid grid-cols-[56px_minmax(0,1fr)] items-center gap-x-3 gap-y-0.5 border-t border-divisor px-3.5 py-2 text-sm md:min-h-fila md:grid-cols-[64px_minmax(0,1fr)_minmax(0,1fr)_auto] md:py-1 ${muted === true ? "text-texto-debil" : "text-texto-2"}`}
                >
                  <span className={`tabular font-semibold ${muted === true ? "text-texto-suave" : "text-texto"}`}>{clock === "" ? "--:--" : clock}</span>
                  <span className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate">{tripTitle(asProviderTrip(row), timezone)}</span>
                    <span className="truncate text-xs text-texto-suave">{row.siteName}</span>
                  </span>
                  <span className="col-start-2 flex min-w-0 flex-col text-xs text-texto-suave md:col-start-auto">
                    <span className="truncate">Vehículo: {vehicle}</span>
                    <span className="truncate">Conductor: {driver}</span>
                  </span>
                  <span className="col-start-2 inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium md:col-start-auto">
                    <i aria-hidden="true" className={`inline-block size-1.5 rounded-full ${stateDot(row)}`} />
                    {stateLabel(row)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );
}

export function Departures({ id, state, rows, timezones, rosters, truncated, now, tripsHref, onRetry }: Props): React.JSX.Element {
  const { upcoming, past } = splitDepartures(rows, now);
  const shown = topN(upcoming, HOME_INFO_ROWS);
  const upcomingGroups = departureGroups(shown.shown, timezones);
  const pastGroups = departureGroups(past, timezones);
  const ready = state.estado === "listo";
  return (
    <Card role="region" aria-labelledby={id} className="gap-0 p-0">
      <PanelHeader id={id} title="Salidas de hoy" count={ready ? rows.length : undefined}>
        {ready && state.stale && <StaleTag />}
        <a href={tripsHref} className="font-medium text-acero-texto">
          Ver el día en Viajes
        </a>
      </PanelHeader>
      {state.estado === "cargando" && <SkeletonTable rows={3} cols={4} label="Consultando los viajes de hoy" />}
      {state.estado === "denegado" && (
        <div className="p-3.5">
          <Failure title="La cuenta no tiene acceso a los viajes de las plantas." />
        </div>
      )}
      {state.estado === "error" && (
        <div className="p-3.5">
          <Failure title="No se pudieron consultar los viajes de hoy." onRetry={onRetry}>
            Se reintenta sola en un minuto.
          </Failure>
        </div>
      )}
      {ready && (
        <>
          {truncated && <p className="m-0 border-b border-divisor px-3.5 py-2 text-xs text-ambar">Hay más viajes de los que se muestran. Revisar con UCORP.</p>}
          {rows.length === 0 ? (
            <p role="status" className="m-0 px-3.5 py-3 text-sm text-texto-suave">
              No hay viajes de la empresa para hoy.
            </p>
          ) : (
            <>
              {upcoming.length === 0 ? (
                <p role="status" className="m-0 px-3.5 py-3 text-sm text-texto-suave">
                  No quedan salidas por delante hoy.
                </p>
              ) : (
                <Groups groups={upcomingGroups} timezones={timezones} rosters={rosters} />
              )}
              {shown.rest > 0 && (
                <p className="m-0 border-t border-divisor px-3.5 py-2 text-xs text-texto-suave">
                  {shown.rest === 1 ? "1 salida más hoy" : `${String(shown.rest)} salidas más hoy`} en Viajes.
                </p>
              )}
              {past.length > 0 && (
                <details className="border-t border-divisor">
                  <summary className="cursor-pointer px-3.5 py-2 text-xs font-medium text-texto-suave hover:text-texto">
                    Ya salieron ({past.length})
                  </summary>
                  <Groups groups={pastGroups} timezones={timezones} rosters={rosters} muted />
                </details>
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
}
